import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Popover } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { nullSafeGetWithZero } from '../../utils/DataAccessUtils';
import { formatLeakRatePercent } from '../../utils/RefrigerantUtils';
import { retrieveCorporateRefrigerantLeakRate } from '../../thunks/refrigerant_events_thunks';
import { withUserType } from '../withUserType';

interface CorporateLeakRateDisplayProps {
	filters?: any;
	fetchCorporateLeakRate?: (filters) => Promise<any>;
}

/**
 * Industry reference points for a corporate leak rate, from the design. Static: they are published
 * programme figures, not anything this tenant's data can move.
 */
const BENCHMARKS = [
	{ label: 'Grocery average', value: '25%' },
	{ label: 'GreenChill partners', value: '13.5%' },
	{ label: 'Best in class', value: 'Under 10%' },
];

const benchmarkContent = (
	<div style={{ maxWidth: 300 }}>
		<div className="bodymedium">Corporate Leak Rate</div>
		<div className="footnoteregular text-character-secondary" style={{ marginBottom: 8 }}>
			Percentage of total refrigerant charge lost to leaks across all tracked facilities, over the
			trailing 12 months.
		</div>
		<div className="bodymedium">Industry benchmarks</div>
		{BENCHMARKS.map((benchmark) => (
			<div key={benchmark.label} className="flex flex-row justify-between gap-4">
				<span className="footnoteregular text-character-secondary">{benchmark.label}</span>
				<span className="footnoteregular text-character-primary">{benchmark.value}</span>
			</div>
		))}
	</div>
);

/**
 * The company-wide leak rate in the Assets sub-tab header.
 *
 * Renders NOTHING when the server has no figure — either the request failed, or nothing in scope
 * carries a full charge, which makes the rate undefined rather than zero. Both are cases where the
 * honest thing is an absent tile: the one thing this must never do is show a number derived from
 * whatever subset of appliances happens to be on screen, because a compliance figure that is quietly
 * wrong is worse than one that is missing.
 */
const CorporateLeakRateDisplay: FC<CorporateLeakRateDisplayProps> = ({
	filters,
	fetchCorporateLeakRate,
}) => {
	const [corporateLeakRate, setCorporateLeakRate] = useState(null);

	// Refetched whenever `filters` changes, not just on mount: the tile sits beside the list and is
	// read as describing it, so a rate left over from the unfiltered estate next to a narrowed result
	// set is a wrong compliance number rather than a stale one. `cancelled` keeps the response that
	// loses a race from overwriting the current filters' figure.
	useEffect(() => {
		let cancelled = false;
		fetchCorporateLeakRate(filters).then((result) => {
			// `nullSafeGetWithZero`, not `nullSafeGet`: 0% is the BEST valid result an estate can report,
			// and a falsy-based read would collapse it to absent and suppress the tile entirely.
			if (!cancelled) setCorporateLeakRate(nullSafeGetWithZero('leakRatePercent', result));
		});
		return () => {
			cancelled = true;
		};
	}, [fetchCorporateLeakRate, filters]);

	if (corporateLeakRate === null || corporateLeakRate === undefined) return null;

	return (
		<div className="flex flex-row items-center gap-2xs">
			<span className="footnoteregular text-character-secondary">Corporate Leak Rate</span>
			<span className="bodymedium text-character-primary">
				{formatLeakRatePercent(corporateLeakRate)}
			</span>
			<Popover content={benchmarkContent} trigger="hover">
				<InfoCircleOutlined className="cursor-pointer text-character-secondary" />
			</Popover>
		</div>
	);
};

const mapStateToProps = () => ({});

// `withUserType` has to wrap `connect` on the outside — mapDispatchToProps reads ownProps.userType.
const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchCorporateLeakRate: (filters) =>
		dispatch(retrieveCorporateRefrigerantLeakRate(ownProps.userType)(filters)),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(CorporateLeakRateDisplay as any) as any
);
